import React from 'react';
import { Link } from 'react-router-dom';
import Moment from 'src/libraries/moment.library';
import {
  ATMButton,
  ATMDatePicker,
  ATMDropdown,
  ATMGrid,
  ATMHeader,
  ATMMenu,
  ATMTab,
  IORGDataTableColumn,
  IORGDataTableQueryState,
  ORGDataTable,
  ATMSegment,
} from 'shared-it-appmod-ui';
import Lang from 'src/libraries/language';
import {
  FLIGHT_FILTERS,
  COMPANY_FILTER,
  RequestFlightPathName,
} from 'src/constants';
import { IRequestItem, IRequestFilters } from 'src/models/request.model';
import { getIsAdmin } from 'src/libraries/access.library';
import StatusRequest from 'src/components/atoms/status-request/status-request.component';
import TableSearch from 'src/components/atoms/table/table-search/table-search.component';
import Tabs from 'src/components/atoms/tabs/tabs.component';
import FlightRequestDropdown from '../flight-request-dropdown/flight-request-dropdown.component';
import FlightRequestListPopover from './flight-request-list-popover/flight-request-list-popover.component';
import style from './flight-request-list.module.scss';

type IProp = {
  data: IRequestItem[];
  filters: IRequestFilters;
  dateRange: string;
  total: number;
  allTotal: number;
  passengerTotal: number;
  constructionTotal: number;
  droneTotal: number;
  loading: boolean;
  downloading: boolean;
  handleFetch: (params: IORGDataTableQueryState) => void;
  handleDownload: (params: IORGDataTableQueryState) => void;
  handleDateRange: (value) => void;
  handleCancel: (data) => void;
  handleCostStatusSuccess: (data) => void;
};

const FlightRequestListView: React.FC<IProp> = ({
  data,
  dateRange,
  filters,
  total,
  allTotal,
  passengerTotal,
  constructionTotal,
  droneTotal,
  loading,
  downloading,
  handleFetch,
  handleDownload,
  handleDateRange,
  handleCancel,
  handleCostStatusSuccess,
}) => {
  const initializedRef = React.useRef(false);

  const customColumns: IORGDataTableColumn<IRequestItem>[] = [
    {
      title: Lang.LBL_REQUEST_INFO,
      index: 'requestId',
      render: (_, value) => (
        <>
          <FlightRequestListPopover data={value} />
        </>
      ),
      width: '20%',
    },
    {
      title: Lang.LBL_FLIGHT_DATE_TIME,
      index: 'flightDateTime',
      render: (_, value) => (
        <span>
          {Moment.utc(value.flightDate).format('MM/DD/YYYY')}
          {` - ${
            value.flightTime?.includes(':')
              ? value.flightTime.replace(':', '')
              : value.flightTime
          } hrs`}
          &nbsp;
          <span
            style={{
              fontSize: '11px',
              fontWeight: 'bold',
              color: value.flightTimeType === 'Requested' ? 'orange' : 'green',
              borderColor:
                value.flightTimeType === 'Requested' ? 'orange' : 'green',
              border: '1.95px solid',
              borderRadius: '4px',
              padding: '0 2px',
            }}
          >
            {value.flightTimeType === 'Requested' ? 'R' : 'A'}
          </span>
        </span>
      ),
      width: '15%',
    },
    {
      title: Lang.LBL_FLIGHT_END_DATE,
      index: 'flightEndDate',
      render: (_, value) => (
        <span>
          {value.flightEndDate
            ? Moment.utc(value.flightEndDate).tz('America/Los_Angeles').format('MM/DD/YYYY')
            : '-'}
        </span>
      ),
      width: '15%',
    },
    {
      title: Lang.LBL_REQUESTOR,
      index: 'requestorName',
    },
    {
      title: Lang.LBL_DP,
      index: 'requestorDistrict',
    },
    {
      title: Lang.LBL_JOB_LOCATION,
      index: 'location',
      render: (_, value) => (
        <span
          style={{
            wordBreak: 'break-word',
          }}
        >
          {value.location}
        </span>
      ),
    },
    {
      title: Lang.LBL_TAIL_NO,
      index: 'tailNumber',
    },
    {
      title: Lang.LBL_FLIGHT_NO,
      index: 'flightNo',
    },
    {
      title: Lang.LBL_FLIGHT_TYPE,
      index: 'flightType',
      render: (_, value) => <span>{value.flightType}</span>,
    },
    {
      title: Lang.LBL_TAR_STATUS,
      index: 'tarStatus',
    },
    {
      title: Lang.LBL_STATUS_ACTIONS,
      index: 'requestStatus',
      width: '200px',
      headerProps: {
        className: 'text-right',
      },
      render: (_, value) => (
        <div className="table-actions right">
          <StatusRequest
            status={value.requestStatus ? value.requestStatus : ''}
          />
          <FlightRequestDropdown
            requestId={value.requestId}
            requestStatus={value.requestStatus}
            requestorNo={value.requestorNo}
            flightCost={value.flightCost}
            type={RequestFlightPathName[value.flightCategoryKey ?? 1]}
            attachmentsCount={value.attachmentsCount}
            handleCancelSuccess={(val) => handleCancel(val)}
            handleCostStatusSuccess={(val) => handleCostStatusSuccess(val)}
          />
        </div>
      ),
    },
  ];

  const dateFilterOptions = getIsAdmin()
    ? FLIGHT_FILTERS
    : FLIGHT_FILTERS.filter((value) => value.key !== 'todaysFlights');

  const table = (
    <ORGDataTable
      celled={false}
      columns={customColumns}
      data={data}
      onChange={handleFetch}
      total={total}
      loading={loading}
      addParams={['ticket', 'company', 'flightRange']}
      rowsPerPageOptions={[50, 75, 100]}
      filteredBy={(filterList) =>
        (filterList || []).map(
          (filter) =>
            Lang.REQUEST_TABLE_FILTER_LABEL[filter.name] ?? filter.name
        )
      }
      sortable
      noDataText={Lang.MSG_NO_RECORDS}
      history
      counter
    >
      {({ state, setState }) => {
        // Initialize flightRange to 'todaysFlights' for admin users on first render
        React.useEffect(() => {
          if (!initializedRef.current && !state.flightRange && getIsAdmin()) {
            initializedRef.current = true;
            handleDateRange(
              `${Moment().format('MM/DD/YYYY')} - ${Moment().format('MM/DD/YYYY')}`
            );
            setState({
              ...state,
              flightRange: 'todaysFlights',
            });
          }
        }, [state, setState]);

        const rangeDisabled =
          (state.flightRange !== 'allFlights' &&
            state.flightRange !== 'futureFlights') ||
          !state.flightRange;

        return {
          toolbars: [
            () => <TableSearch state={state} setState={setState} />,
            () => (
              <ATMDropdown
                selection
                value={state.company ?? COMPANY_FILTER[0].value}
                options={COMPANY_FILTER}
                onChange={(_, val) => {
                  if (val.value !== COMPANY_FILTER[0].value) {
                    setState({
                      ...state,
                      page: 1,
                      company: val.value,
                    });
                  } else {
                    setState({
                      ...state,
                      page: 1,
                      company: undefined,
                    });
                  }
                }}
              />
            ),
            () => (
              <ATMDropdown
                selection
                value={
                  state.flightRange ||
                  (getIsAdmin() ? 'todaysFlights' : 'allFlights')
                }
                options={dateFilterOptions}
                onChange={(_, val) => {
                  switch (val.value) {
                    case 'todaysFlights': {
                      handleDateRange(
                        `${Moment().format('MM/DD/YYYY')} - ${Moment().format(
                          'MM/DD/YYYY'
                        )}`
                      );

                      break;
                    }

                    case 'last30DaysFlights': {
                      handleDateRange(
                        `${Moment()
                          .subtract(30, 'days')
                          .format('MM/DD/YYYY')} - ${Moment().format(
                          'MM/DD/YYYY'
                        )}`
                      );
                      break;
                    }

                    case 'futureFlights': {
                      handleDateRange(
                        `${Moment()
                          .add(1, 'day')
                          .format('MM/DD/YYYY')} - ${Moment()
                          .add(730, 'days')
                          .format('MM/DD/YYYY')}`
                      );

                      break;
                    }

                    default: {
                      handleDateRange('Select');
                      break;
                    }
                  }

                  const params = {
                    ...state,
                    page: 1,
                    flightRange: val.value,
                  };

                  if (params.flightRange === 'futureFlights') {
                    params.order = 'ascending';
                    params.sort = 'flightDateTime';
                  }

                  setState(params);

                  return val.value;
                }}
              />
            ),
            () => (
              <ATMButton
                basic
                color="blue"
                icon="download"
                content={Lang.LBL_DOWLOAD}
                floated="right"
                loading={downloading}
                disabled={downloading || total === 0}
                onClick={() => handleDownload(state)}
              />
            ),
          ],
          filters: getIsAdmin()
            ? ({ values, setValue }) => {
                return (
                  <ATMGrid stretched doubling stackable>
                    <ATMGrid.Row columns="equal">
                      <ATMGrid.Column>
                        <ATMHeader
                          as="h3"
                          content={Lang.LBL_DISTRICT_DEPT_PROJECT}
                        />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          value={values.requestorDistrict}
                          options={filters.districts?.map((dist) => ({
                            key: dist?.replace(' ', ''),
                            value: dist,
                            text: dist,
                          }))}
                          onChange={(_, val) =>
                            setValue('requestorDistrict', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_STATUS} />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          value={values.requestStatus}
                          options={filters.requestStatuses?.map((status) => ({
                            key: status?.replace(' ', ''),
                            value: status,
                            text: status,
                          }))}
                          onChange={(_, val) =>
                            setValue('requestStatus', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_FLIGHT_TYPE} />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          value={values.flightType}
                          options={filters.flightTypes?.map((type) => ({
                            key: type?.replace(' ', ''),
                            value: type,
                            text: type,
                          }))}
                          onChange={(_, val) =>
                            setValue('flightType', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_OPERATOR} />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          value={values.operatorName}
                          options={filters.operatorNames?.map((operator) => ({
                            key: operator?.replace(' ', ''),
                            value: operator,
                            text: operator,
                          }))}
                          onChange={(_, val) =>
                            setValue('operatorName', val.value)
                          }
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row columns="equal">
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_TAIL_NO} />
                        <ATMDropdown
                          selection
                          clearable
                          multiple
                          value={values.tailNumber}
                          options={filters.tailNumbers?.map((tail) => ({
                            key: tail?.replace(' ', ''),
                            value: tail,
                            text: tail,
                          }))}
                          onChange={(_, val) =>
                            setValue('tailNumber', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column className={style.datepicker}>
                        <ATMHeader
                          as="h3"
                          content={Lang.LBL_FLIGHT_DATE_RANGE}
                        />
                        <ATMDatePicker
                          type="range"
                          format="MM/DD/YYYY"
                          className={rangeDisabled ? style.disabledPicker : ''}
                          value={values.flightDate?.map(
                            (flightDate) => new Date(flightDate)
                          )}
                          placeholder={dateRange}
                          disabled={rangeDisabled}
                          onChange={(_, val) =>
                            setValue('flightDate', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_TAR_STATUS} />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          value={values.tarStatus}
                          options={filters.tarStatuses?.map((tar) => ({
                            key: tar?.replace(' ', ''),
                            value: tar,
                            text: tar,
                          }))}
                          onChange={(_, val) =>
                            setValue('tarStatus', val.value)
                          }
                        />
                      </ATMGrid.Column>
                      <ATMGrid.Column>
                        <ATMHeader as="h3" content={Lang.LBL_REQUESTED_BY} />
                        <ATMDropdown
                          selection
                          multiple
                          clearable
                          search
                          value={values.requestorName}
                          options={filters.requestorNames?.map((requestor) => ({
                            key: requestor?.replace(' ', ''),
                            value: requestor,
                            text: requestor,
                          }))}
                          onChange={(_, val) =>
                            setValue('requestorName', val.value)
                          }
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                  </ATMGrid>
                );
              }
            : undefined,
        };
      }}
    </ORGDataTable>
  );

  const panes = [
    {
      menuItem: (
        <ATMMenu.Item key="All">
          {Lang.formatString(Lang.LBL_ALL, allTotal)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Passenger">
          {Lang.formatString(Lang.LBL_PASSENGER_FLIGHTS, passengerTotal)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Construction">
          {Lang.formatString(Lang.LBL_CONSTRUCTION_FLIGHTS, constructionTotal)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Helicopter">
          {Lang.formatString(
            Lang.LBL_HELICOPTER_FLIGHTS,
            constructionTotal + passengerTotal
          )}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
    {
      menuItem: (
        <ATMMenu.Item key="Drone">
          {Lang.formatString(Lang.LBL_DRONE_FLIGHTS, droneTotal)}
        </ATMMenu.Item>
      ),
      render: () => <ATMTab.Pane attached={false}>{table}</ATMTab.Pane>,
    },
  ];

  return (
    <>
      <ATMGrid>
        <ATMGrid.Row columns="equal">
          <ATMGrid.Column>
            <ATMHeader as="h1" content={Lang.TTL_AVIATION_SERVICE_REQUEST} />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <ATMButton
              basic
              color="blue"
              icon="plus"
              content={Lang.LBL_ADD_FLIGHT_REQUEST}
              floated="right"
              as={Link}
              to="/flight/request"
            />
          </ATMGrid.Column>
        </ATMGrid.Row>
        <ATMGrid.Row>
          <ATMGrid.Column>
            {getIsAdmin() ? (
              <div className={style.proTabs}>
                <Tabs menu={{ pointing: true }} panes={panes} />
              </div>
            ) : (
              <ATMSegment>{table}</ATMSegment>
            )}
          </ATMGrid.Column>
        </ATMGrid.Row>
      </ATMGrid>
    </>
  );
};

export default FlightRequestListView;
